import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from './api';

export interface UserSummary {
  id: number;
  email: string;
  name: string | null;
  is_verified: boolean;
  is_superuser: boolean;
}

export interface OrganizationMembership {
  id: number;
  name: string;
  slug: string;
  role: string;
}

export interface MeResponse {
  user: UserSummary;
  organizations: OrganizationMembership[];
  system_roles: string[];
}

export const ME_QUERY_KEY = ['me'] as const;

export function useMe() {
  return useQuery<MeResponse | null>({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      try {
        return await api.get<MeResponse>('/api/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 30_000,
    retry: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      const body = new URLSearchParams({
        username: input.email,
        password: input.password,
      });
      await api.postForm<unknown>('/api/auth/login', body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.postJson<unknown>('/api/auth/logout', {}),
    onSettled: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string; name?: string }) =>
      api.postJson<UserSummary>('/api/auth/register', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });
}

export function useForgotPassword() {
  // Backend always returns 202 to avoid leaking which addresses exist.
  return useMutation({
    mutationFn: (email: string) =>
      api.postJson<unknown>('/api/auth/forgot-password', { email }),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      api.postJson<unknown>('/api/auth/reset-password', input),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name?: string; password?: string }) =>
      api.patchJson<UserSummary>('/api/users/me', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });
}
